// All comments in English.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { getProducts, type ProductOption } from "@/autodesk_usage/api/meta";

export interface ProductSelectProps {
  value: string | null;
  onChange: (next: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  /** If provided, skip API call and use these options instead. */
  options?: ProductOption[];
  /** Auto focus on mount. */
  autoFocus?: boolean;
}

/** A reusable type-in dropdown (combobox) for selecting Autodesk product. */
export default function ProductSelect({
  value,
  onChange,
  placeholder = "Search product...",
  disabled,
  options,
  autoFocus,
}: ProductSelectProps) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [remoteOptions, setRemoteOptions] = useState<ProductOption[]>([]);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const [activeIndex, setActiveIndex] = useState<number>(-1);

  // Load from API only when options prop is not provided.
useEffect(() => {
  if (options && Array.isArray(options)) return;
  let mounted = true;
  setLoading(true);
  getProducts()
    .then((rows: any) => {
      if (!mounted) return;
      // Normalize to array: accept both array and { items: [...] }
      const arr = Array.isArray(rows)
        ? rows
        : Array.isArray(rows?.items)
        ? rows.items
        : [];
      setRemoteOptions(arr);
    })
    .finally(() => mounted && setLoading(false));
  return () => {
    mounted = false;
  };
}, [options]);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  const allOptions = options ?? remoteOptions;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allOptions;
    return allOptions.filter((o) => o.product.toLowerCase().includes(q));
  }, [allOptions, query]);

  // Keep input text in sync with value when user selects an option.
  useEffect(() => {
    if (value) setQuery(value);
  }, [value]);

  function handleSelect(p: string) {
    onChange(p);
    setQuery(p);
    setOpen(false);
  }

  function clearSelection() {
    onChange(null);
    setQuery("");
    setActiveIndex(-1);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      setOpen(true);
      return;
    }
    if (!open) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const opt = filtered[activeIndex];
      if (opt) handleSelect(opt.product);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  return (
    <div style={{ position: "relative", maxWidth: 560 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          padding: "10px 12px",
          background: disabled ? "#f9fafb" : "white",
          boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
        }}
        onClick={() => !disabled && setOpen(true)}
      >
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setActiveIndex(-1);
          }}
          onFocus={() => !disabled && setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          style={{
            flex: 1,
            border: "none",
            outline: "none",
            fontSize: 16,
            background: "transparent",
          }}
        />
        {loading ? (
          <span style={{ fontSize: 12, color: "#9ca3af" }}>Loading…</span>
        ) : value ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              clearSelection();
              inputRef.current?.focus();
            }}
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
              color: "#6b7280",
              fontSize: 14,
            }}
            aria-label="Clear selection"
          >
            ✕
          </button>
        ) : (
          <span style={{ color: "#9ca3af" }}>▾</span>
        )}
      </div>

      {open && !disabled && (
        <ul
          ref={listRef}
          role="listbox"
          style={{
            position: "absolute",
            zIndex: 20,
            marginTop: 6,
            maxHeight: 280,
            overflowY: "auto",
            width: "100%",
            background: "white",
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
          }}
          onMouseLeave={() => setActiveIndex(-1)}
        >
          {filtered.length === 0 ? (
            <li
              style={{
                padding: "10px 12px",
                color: "#6b7280",
                fontSize: 14,
              }}
            >
              No results
            </li>
          ) : (
            filtered.map((o, idx) => {
              const isActive = idx === activeIndex;
              const isSelected = value === o.product;
              return (
                <li
                  key={o.product}
                  role="option"
                  aria-selected={isSelected}
                  onMouseEnter={() => setActiveIndex(idx)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSelect(o.product)}
                  style={{
                    padding: "10px 12px",
                    cursor: "pointer",
                    background: isActive
                      ? "#f3f4f6"
                      : isSelected
                      ? "#eef2ff"
                      : "transparent",
                    fontWeight: isSelected ? 600 : 400,
                  }}
                >
                  {o.product}
                </li>
              );
            })
          )}
        </ul>
      )}
    </div>
  );
}
